import { CRS, type FitBoundsOptions, geoJSON, Icon, latLng, latLngBounds, Point } from 'leaflet';
import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import { Circle, Marker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import { Box } from '@mui/material';
import markerIconPng from 'leaflet/dist/images/marker-icon.png';
import { pick } from 'lodash';
import HoverCard from '../HoverCard';
import MapZoomControls from '../MapZoomControls';
import type { LeafletMapProps, PaCommonMapHoverTarget, PaCommonMapProps } from '../types';
import useParcelHoverController from '../useParcelHoverController';
import GestureHandlingControl from './GestureHandlingControl';
import LeafletMap from './LeafletMap';
import ToolbarLayer from './ToolbarLayer';

const markerIcon = new Icon({ iconUrl: markerIconPng, iconSize: [25, 41] });

function getHoverTargetKey(target: PaCommonMapHoverTarget) {
  const { item, kind } = target;
  return item.hoverId ?? `${kind}:${item.lat},${item.lng}`;
}

type Props = PaCommonMapProps & {
  zoomState: [number, Dispatch<SetStateAction<number>>];
};

function FitMapPointBounds({
  points,
  options,
}: Readonly<{ points: Array<[number, number]>; options?: FitBoundsOptions }>) {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return;
    map.fitBounds(latLngBounds(points), options);
  }, [map, options, points]);

  return null;
}

const MapContent = ({
  zoomState,
  center,
  mapCoordinates,
  defaultZoom = 12,
  displayCenterMarker,
  visualEsr,
  loading,
  height,
  width,
  circles,
  markers,
  toolbarOptions,
  enableEdit,
  getPropertiesOnAdd,
  addShapeOptions,
  disableAdding,
  disabledButtons,
  hoverContent,
  hoverCardRightInset = 0,
  disableZoomLock,
  fitMapPoints,
  mapPointFitBoundsOptions,
  ...layerProps
}: Readonly<Props>) => {
  const [, setZoom] = zoomState;
  const { state: hoverState, handlers: hoverHandlers } = useParcelHoverController<
    PaCommonMapHoverTarget,
    'circle' | 'marker'
  >({
    getItemKey: getHoverTargetKey,
    hoverEndDelay: 150,
  });

  const visualEsrBounds = useMemo(() => {
    if (!visualEsr) return undefined;
    const minBound = CRS.EPSG3857.unproject(
      new Point(visualEsr.bounds.x[0], visualEsr.bounds.y[0]),
    );
    const maxBound = CRS.EPSG3857.unproject(
      new Point(visualEsr.bounds.x[1], visualEsr.bounds.y[1]),
    );

    return {
      ...visualEsr,
      bounds: { x: [minBound.lat, minBound.lng], y: [maxBound.lat, maxBound.lng] },
    } satisfies LeafletMapProps['visualEsr'];
  }, [visualEsr]);

  const allMarkers = useMemo(() => {
    const circleKeyCounts = new Map<string, number>();
    const displayMarkers = circles?.map((circle) => {
      const { click, hoverId: _hoverId, lat, lng, tooltip, ...circleProps } = circle;
      const hoverTarget: PaCommonMapHoverTarget = { item: circle, kind: 'circle' };
      const baseCircleKey = circle.hoverId ?? `circle-${lat}-${lng}-${circle.radius ?? 50000}`;
      const duplicateCount = circleKeyCounts.get(baseCircleKey) ?? 0;
      circleKeyCounts.set(baseCircleKey, duplicateCount + 1);
      const circleKey =
        duplicateCount === 0 ? baseCircleKey : `${baseCircleKey}-duplicate-${duplicateCount}`;

      return (
        <Circle
          {...circleProps}
          key={circleKey}
          center={pick(circle, 'lat', 'lng')}
          pathOptions={circleProps}
          radius={circle.radius ?? 50000}
          eventHandlers={{
            click: (event) => {
              click?.(event);
            },
            mouseover: (event) => {
              if (!hoverContent) return;
              hoverHandlers.onHoverStart(hoverTarget, event.latlng ?? latLng(lat, lng), 'circle');
            },
            mouseout: () => {
              if (!hoverContent) return;
              hoverHandlers.onHoverEnd();
            },
          }}
        >
          {!hoverContent && tooltip ? <Tooltip sticky>{tooltip}</Tooltip> : undefined}
        </Circle>
      );
    });

    const markerKeyCounts = new Map<string, number>();
    const otherMarkers = markers?.map((marker) => {
      const { click, hoverId: _hoverId, icon, lat, lng, tooltip, ...markerProps } = marker;
      const hoverTarget: PaCommonMapHoverTarget = { item: marker, kind: 'marker' };
      const baseMarkerKey = marker.hoverId ?? `marker-${lat}-${lng}`;
      const duplicateCount = markerKeyCounts.get(baseMarkerKey) ?? 0;
      markerKeyCounts.set(baseMarkerKey, duplicateCount + 1);
      const markerKey =
        duplicateCount === 0 ? baseMarkerKey : `${baseMarkerKey}-duplicate-${duplicateCount}`;

      return (
        <Marker
          key={markerKey}
          position={[lat, lng]}
          icon={icon ?? markerIcon}
          {...markerProps}
          eventHandlers={{
            click: (event) => {
              click?.(event);
            },
            mouseover: (event) => {
              if (!hoverContent) return;
              hoverHandlers.onHoverStart(hoverTarget, event.latlng ?? latLng(lat, lng), 'marker');
            },
            mouseout: () => {
              if (!hoverContent) return;
              hoverHandlers.onHoverEnd();
            },
          }}
        >
          {!hoverContent && tooltip ? <Tooltip sticky>{tooltip}</Tooltip> : undefined}
        </Marker>
      );
    });

    if (!displayCenterMarker || !center) {
      return [...(otherMarkers ?? []), ...(displayMarkers ?? [])];
    }

    return [
      <Marker position={center} icon={markerIcon} key={JSON.stringify(center)} />,
      ...(otherMarkers ?? []),
      ...(displayMarkers ?? []),
    ];
  }, [center, circles, displayCenterMarker, hoverContent, hoverHandlers, markers]);

  const renderedHoverContent = hoverContent?.(hoverState) ?? null;
  const mapPoints = useMemo<Array<[number, number]>>(
    () => [
      ...(markers ?? []).map((marker): [number, number] => [marker.lat, marker.lng]),
      ...(circles ?? []).map((circle): [number, number] => [circle.lat, circle.lng]),
    ],
    [circles, markers],
  );
  const projectParcelFullExtent = useMemo(() => {
    if (!layerProps.fitProjectParcels || !layerProps.projectParcelsBuildable) return undefined;
    const bounds = geoJSON(layerProps.projectParcelsBuildable.json).getBounds();
    return bounds.isValid() ? bounds : undefined;
  }, [layerProps.fitProjectParcels, layerProps.projectParcelsBuildable]);

  return (
    <>
      <LeafletMap {...layerProps} visualEsr={visualEsrBounds} />
      {fitMapPoints && <FitMapPointBounds points={mapPoints} options={mapPointFitBoundsOptions} />}
      <GestureHandlingControl enabled={!disableZoomLock} />
      {enableEdit && (
        <ToolbarLayer
          enableEdit={enableEdit}
          disableAdding={disableAdding}
          getPropertiesOnAdd={getPropertiesOnAdd}
          addShapeOptions={addShapeOptions}
          options={toolbarOptions}
          disabledButtons={disabledButtons}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <MapZoomControls
          center={center}
          defaultZoom={defaultZoom}
          fullExtentBounds={projectParcelFullExtent}
          fullExtentFitBoundsOptions={layerProps.projectParcelsFitBoundsOptions}
          onZoomChange={setZoom}
        />
      </Box>
      {allMarkers}
      {renderedHoverContent && (
        <HoverCard
          hoverAnchor={hoverState.hoverAnchor}
          rightInset={hoverCardRightInset}
          onMouseEnter={hoverHandlers.onCardMouseEnter}
          onMouseLeave={hoverHandlers.onCardMouseLeave}
        >
          {renderedHoverContent}
        </HoverCard>
      )}
    </>
  );
};

export default MapContent;
